import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getJewelleryRequest } from "../../redux/Jewellery/jewellerySlice";
import {
  addJewelleryToCartRequest,
  fetchJewelleryCartRequest,
} from "../../redux/Jewellery/jewelleryCartSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";
import {
  asList,
  collectionName,
  firstImage,
  matchesOccasion,
  availableInventory,
  priceBreakdown,
  stockLabel,
  typeName,
  uniqueSorted,
} from "./jewelleryUtils";

function ImageFallback({ src, alt, height = 160 }) {
  const [err, setErr] = useState(false);
  return (
    <img
      src={err || !src ? "/images/logo.png" : src}
      alt={alt || "artificial jewellery"}
      style={{
        width: "100%",
        height,
        objectFit: "cover",
        display: "block",
        background: "#f4f4f4",
      }}
      onError={() => setErr(true)}
    />
  );
}

const selectSx = {
  color: "#fff",
  background: "rgba(255,255,255,0.1)",
  borderRadius: 2,
  ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.45)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
  ".MuiSvgIcon-root": { color: "#fff" },
};

function FilterSelect({ label, value, options, onChange }) {
  return (
    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
      <InputLabel
        sx={{
          color: "rgba(255,255,255,0.9)",
          "&.Mui-focused": { color: "#fff" },
        }}
      >
        {label}
      </InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        sx={selectSx}
        MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
      >
        <MenuItem value="All">All</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const stockColor = (label) => {
  if (label === "In Stock") return "#8dffb0";
  if (label === "Made-to-order") return "#ffc46b";
  return "#d0d0d0";
};

function ProductCard({ item, onClick, onAdd, symbol, themeColors }) {
  const price = priceBreakdown(item, 1);
  const label = stockLabel(item);
  return (
    <Card
      onClick={onClick}
      sx={{
        // borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
        cursor: "pointer",
        height: "100%",
        position: "relative",
      }}
    >
      {Number(item.discount) > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 20,
            zIndex: 1,
            bgcolor: "#3DDC84",
            color: "#063",
            px: 1,
            py: 0.25,
            borderRadius: 2,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {Number(item.discount)}% off
        </Box>
      )}
      <ImageFallback src={firstImage(item)} alt={item.name} height={220} />
      <CardContent
        sx={{
          background: themeColors.mediumContainer,
          color: themeColors.text,
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {item.name}
          </Typography>
          {Number(item.discount) > 0 && (
            <Typography
              sx={{
                color: "#3DDC84",
                fontWeight: 800,
                fontSize: 14,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              ↓ {Number(item.discount)}%
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" sx={{ display: "block" }} noWrap>
          {[(item.subType || "").trim(), collectionName(item), ...asList(item.occasion)]
            .filter(Boolean)
            .join(" • ")}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }}>
          <Typography
            variant="h7"
            sx={{ textDecoration: "line-through", opacity: 0.7 ,color: themeColors.amountOption}}
          >
            {symbol}
            {price.mrp.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </Typography>
          <Typography variant="h6">
            {symbol}
            {price.unitDiscounted.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </Typography>
        </Stack>
        
        <Typography
          variant="caption"
          sx={{ display: "block", color: stockColor(label), fontWeight: 700 }}
        >
          {label}
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={label === "Out of stock"}
          onClick={(event) => {
            event.stopPropagation();
            onAdd();
          }}
          sx={{
            mt: 1,
            textTransform: "none",
            background: themeColors.primary,
            color: themeColors.text,
          }}
        >
          Add to cart
        </Button>
      </CardContent>
    </Card>
  );
}

export default function JewelleryView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeColors = useAppTheme();
  const { symbol } = useCurrency();
  const { loading, list } = useSelector((state) => state.jewellery || {});
  const cartItems = useSelector((state) => state.jewelleryCart?.items || []);
  const cartCount = cartItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0,
  );
  const [selectedType, setSelectedType] = useState("");
  const [search, setSearch] = useState("");
  const [subType, setSubType] = useState("All");
  const [collection, setCollection] = useState("All");
  const [occasion, setOccasion] = useState("All");

  useEffect(() => {
    dispatch(getJewelleryRequest());
    dispatch(fetchJewelleryCartRequest());
  }, [dispatch]);

  const items = useMemo(() => {
    const unique = new Map();
    (Array.isArray(list) ? list : []).forEach((item) => {
      if (item?._id) unique.set(String(item._id), item);
    });
    return [...unique.values()];
  }, [list]);

  const types = useMemo(() => {
    return uniqueSorted(items.map(typeName)).map((name) => {
      const products = items.filter((item) => typeName(item) === name);
      return {
        name,
        image: firstImage(products[0]),
        count: products.length,
      };
    });
  }, [items]);

  const typeProducts = useMemo(
    () => items.filter((item) => typeName(item) === selectedType),
    [items, selectedType],
  );

  const subTypeOptions = useMemo(
    () =>
      uniqueSorted(
        typeProducts
          .filter((item) => collection === "All" || collectionName(item) === collection)
          .filter((item) => matchesOccasion(item, occasion))
          .map((item) => (item.subType || "").trim()),
      ),
    [typeProducts, collection, occasion],
  );

  const collectionOptions = useMemo(
    () =>
      uniqueSorted(
        typeProducts
          .filter(
            (item) => subType === "All" || (item.subType || "").trim() === subType,
          )
          .filter((item) => matchesOccasion(item, occasion))
          .map(collectionName),
      ),
    [typeProducts, subType, occasion],
  );

  const occasionOptions = useMemo(
    () =>
      uniqueSorted(
        typeProducts
          .filter(
            (item) => subType === "All" || (item.subType || "").trim() === subType,
          )
          .filter((item) => collection === "All" || collectionName(item) === collection)
          .flatMap((item) => asList(item.occasion)),
      ),
    [typeProducts, subType, collection],
  );

  useEffect(() => {
    if (subType !== "All" && !subTypeOptions.includes(subType)) setSubType("All");
  }, [subType, subTypeOptions]);

  useEffect(() => {
    if (collection !== "All" && !collectionOptions.includes(collection)) {
      setCollection("All");
    }
  }, [collection, collectionOptions]);

  useEffect(() => {
    if (occasion !== "All" && !occasionOptions.includes(occasion)) setOccasion("All");
  }, [occasion, occasionOptions]);

  const visibleProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return typeProducts.filter((item) => {
      if (subType !== "All" && (item.subType || "").trim() !== subType) return false;
      if (collection !== "All" && collectionName(item) !== collection) return false;
      if (!matchesOccasion(item, occasion)) return false;
      if (!query) return true;
      return [item.name, item.title, item.productCode, item.designStyle]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [typeProducts, subType, collection, occasion, search]);

  const openType = (name) => {
    setSelectedType(name);
    setSearch("");
    setSubType("All");
    setCollection("All");
    setOccasion("All");
  };

  const goHome = () => {
    setSelectedType("");
    setSearch("");
    setSubType("All");
    setCollection("All");
    setOccasion("All");
  };

  const addToCart = (item) => {
    const row = availableInventory(item)[0];
    if (!row) {
      toast.error("This piece is out of stock");
      return;
    }
    const stock = Number(row.quantity);
    const cap = Number.isFinite(stock) && stock > 0 ? Math.floor(stock) : 1;
    const existing = cartItems.find((line) => line.inventoryId === String(row._id));
    if (existing && existing.quantity >= cap) {
      toast.info("Maximum quantity for this piece is already in your cart");
      return;
    }
    dispatch(
      addJewelleryToCartRequest({
        jewellery: item._id,
        inventory: row._id,
        quantity: 1,
      }),
    );
  };

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
        color: themeColors.text,
      }}
    >
      <AppBar position="sticky" elevation={0} sx={{ background: themeColors.appBar }}>
        <Toolbar>
          {selectedType && (
            <IconButton onClick={goHome} aria-label="Back">
              <ArrowBackIcon sx={{ color: themeColors.primary }} />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, color: themeColors.primary }}>
            {selectedType || "Artificial Jewellery"}
          </Typography>
          <IconButton aria-label="Cart" onClick={() => navigate("/artificialJewelleryCart")}>
            <Badge badgeContent={cartCount} color="error">
              <ShoppingCartIcon sx={{ color: themeColors.primary }} />
            </Badge>
          </IconButton>
          <IconButton
            aria-label="Artificial jewellery orders"
            onClick={() => navigate("/artificialJewelleryOrder")}
          >
            <ShoppingBagIcon sx={{ color: themeColors.primary }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container sx={{ py: 3 }}>
        {loading && !items.length ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        ) : null}

        {!loading && !items.length ? (
          <Typography textAlign="center" sx={{ mt: 6 }}>
            No artificial jewellery available
          </Typography>
        ) : null}

        {!selectedType && items.length > 0 && (
          <Grid container spacing={2}>
            {types.map((item) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.name}>
                <Card
                  onClick={() => openType(item.name)}
                  sx={{
                    overflow: "hidden",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                    cursor: "pointer",
                    height: "100%",
                  }}
                >
                  <ImageFallback src={item.image} alt={item.name} height={250} />
                  <CardContent
                    sx={{
                      textAlign: "center",
                      background: themeColors.mediumContainer,
                      color: themeColors.text,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {item.name}
                    </Typography>
                    <Typography variant="caption">
                      {item.count} piece{item.count === 1 ? "" : "s"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {selectedType && (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder={`Search in ${selectedType}`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: 2,
                  "& fieldset": { borderColor: "rgba(255,255,255,0.45)" },
                  "&:hover fieldset": { borderColor: "#fff" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#fff" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
              <FilterSelect
                label="Style"
                value={subTypeOptions.includes(subType) ? subType : "All"}
                options={subTypeOptions}
                onChange={setSubType}
              />
              <FilterSelect
                label="Collection"
                value={collectionOptions.includes(collection) ? collection : "All"}
                options={collectionOptions}
                onChange={setCollection}
              />
              <FilterSelect
                label="Occasion"
                value={occasionOptions.includes(occasion) ? occasion : "All"}
                options={occasionOptions}
                onChange={setOccasion}
              />
            </Stack>

            {visibleProducts.length === 0 ? (
              <Typography textAlign="center" sx={{ mt: 5 }}>
                No artificial jewellery found
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {visibleProducts.map((item) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item._id}>
                    <ProductCard
                      item={item}
                      symbol={symbol}
                      themeColors={themeColors}
                      onAdd={() => addToCart(item)}
                      onClick={() =>
                        navigate("/artificialJewelleryDetails", {
                          state: { jewellery: item },
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
